import compact from "lodash/compact";
import difference from "lodash/difference";
import entries from "lodash/entries";
import flatMap from "lodash/flatMap";
import keys from "lodash/keys";
import map from "lodash/map";
import omit from "lodash/omit";
import uniq from "lodash/uniq";
import { getAllFrameworksCustomTypeValues } from "PFApp/activities/activity_form/helpers";
import { useFormSelectedSkillsFrameworks } from "PFApp/activities/activity_form/hooks/use_form_selected_skills_frameworks";
import { FIELD_CUSTOM_FIELDS_KEY } from "PFApp/constants/activity_form/activity_form_field_keys";
import { useGrowlStoreActions } from "PFApp/stores/growl_store";
import { useCustomTypes } from "PFCore/helpers/use_custom_types";
import { ActivityPureCustomValue, SkillsFramework, SkillsFrameworkSkill } from "PFCore/types";
import { useEffect, useRef } from "react";
import { useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

const EXTRA_SKILLS_FRAMEWORK_SKILLS_LIMIT = 3;

export const useSkillsFrameworksWarningAlert = () => {
  const { t } = useTranslation("activities", { keyPrefix: "form.sections.skillsFrameworks" });
  const { showGrowl } = useGrowlStoreActions();
  const { selectedSkillsFrameworks, isSkillsFrameworksLoading } = useFormSelectedSkillsFrameworks();
  const { getCustomTypeByName } = useCustomTypes();

  const customFields = useWatch({ name: FIELD_CUSTOM_FIELDS_KEY });
  const isAlertShown = useRef(false);
  const isInitialCheckDone = useRef(false);

  const checkFrameworkMatched = (
    customFields: Record<string, ActivityPureCustomValue[]>,
    skillsFrameworks: SkillsFramework[]
  ): boolean => {
    const formSkillsValues = customFields?.skills || [];
    const formCustomFields = omit(customFields, "skills");
    const formSkillsValuesIds = formSkillsValues.map(({ id }) => id);

    const allFrameworksSkills: SkillsFrameworkSkill[] = flatMap(skillsFrameworks, "skills");

    const allFrameworkSkillsUniqueIds = uniq(map(allFrameworksSkills, "skillId"));
    const extraSkillsIds = difference(formSkillsValuesIds, allFrameworkSkillsUniqueIds);

    const skillsMatchFramework =
      // all frameworks skills are present in the form
      difference(allFrameworkSkillsUniqueIds, formSkillsValuesIds).length === 0 &&
      // there are no more than 3 additional skills in the form
      extraSkillsIds.length <= EXTRA_SKILLS_FRAMEWORK_SKILLS_LIMIT &&
      // for every non-additional skill in the form, it is the only variant in all frameworks
      // (if there are a few skill variants, then form doesn't match the frameworks)
      formSkillsValues
        .filter(({ id }) => !extraSkillsIds.includes(id))
        .every(({ id, required_experience, importance }) => {
          const skillVariants = allFrameworksSkills.filter((frameworkSkill) => frameworkSkill.skillId === id);

          return (
            skillVariants.length > 0 &&
            skillVariants.every(
              (frameworkSkill) =>
                frameworkSkill.requiredExperience === required_experience &&
                frameworkSkill.importance === importance
            )
          );
        });

    const allFrameworksCustomFieldsIds = uniq(
      map(flatMap(skillsFrameworks, "skillsFrameworkCustomTypes"), "customTypeId")
    );
    const formCustomFieldsNames = keys(formCustomFields);
    const formCustomFieldsIds = compact(formCustomFieldsNames.map((name) => getCustomTypeByName(name)?.id));

    const customValuesMatchFramework =
      // custom fields from frameworks are present in the form
      difference(allFrameworksCustomFieldsIds, formCustomFieldsIds).length === 0 &&
      // CF values are exactly the same as all the CF values in the frameworks
      entries(formCustomFields)
        .filter(([fieldName]) => {
          const customType = getCustomTypeByName(fieldName);
          return customType && allFrameworksCustomFieldsIds.includes(customType.id);
        })
        .every(([fieldName, values]) => {
          const customType = getCustomTypeByName(fieldName)!;
          const allFrameworksCustomValuesIds = getAllFrameworksCustomTypeValues(
            skillsFrameworks,
            customType.id
          );
          const formValuesIds = map(values, "id");

          return (
            difference(allFrameworksCustomValuesIds, formValuesIds).length === 0 &&
            difference(formValuesIds, allFrameworksCustomValuesIds).length === 0
          );
        });

    return skillsMatchFramework && customValuesMatchFramework;
  };

  const resetWarningEffect = () => {
    isAlertShown.current = false;
  };

  useEffect(() => {
    if (isSkillsFrameworksLoading) {
      return;
    }
    if (selectedSkillsFrameworks.length === 0) {
      isInitialCheckDone.current = true;
      return;
    }
    if (!isInitialCheckDone.current && !checkFrameworkMatched(customFields, selectedSkillsFrameworks)) {
      // If initially there are frameworks that are not matching the form, assume that alert was shown before
      isAlertShown.current = true;
    }
    isInitialCheckDone.current = true;
  }, [selectedSkillsFrameworks]);

  useEffect(() => {
    if (selectedSkillsFrameworks.length === 0) {
      return;
    }

    if (!isAlertShown.current && !checkFrameworkMatched(customFields, selectedSkillsFrameworks)) {
      showGrowl({
        message: t("skillFrameworkUnlinkedWarning"),
        kind: "alert"
      });
      isAlertShown.current = true;
    }
  }, [customFields, selectedSkillsFrameworks]);

  return {
    resetWarningEffect
  };
};
